import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FileType,
  Image,
  Video,
  Music,
  FileSpreadsheet,
  Archive,
  File,
  Code,
} from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/constants";

// Interfaces
interface Resource {
  id: number;
  title: string;
  description: string;
  type: string;
  image: string;
  badge?: string;
  moduleId?: string;
  moduleTitle?: string;
  fileUrl?: string;
  filename?: string; // Nom du fichier avec extension
  mimetype?: string; // Type MIME du fichier
}

interface Module {
  id: string;
  title: string;
  bgColor: string;
  resources: Resource[];
}

// Interface pour le module récupéré de l'API
interface ModuleData {
  id: string;
  title: string;
  order?: number;
  description?: string;
  content?: unknown; // Propriété de contenu de type inconnu
  createdAt?: string;
  updatedAt?: string;
}

const RessourcesPedagogiques: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Récupération des ressources depuis l'API
  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true);
      setError(null);

      try {
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
          throw new Error("Vous n'êtes pas connecté");
        }

        // Récupérer tous les modules pour avoir leurs titres
        const modulesResponse = await fetch(
          `${API_ENDPOINTS.CINEPRO.GET_MODULES}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!modulesResponse.ok) {
          console.warn("Impossible de récupérer les modules");
        }

        // Créer un mapping des modules pour récupérer leurs titres
        const modulesData = await modulesResponse.json();
        const moduleIdToTitle = new Map();

        if (Array.isArray(modulesData)) {
          modulesData.forEach((module: ModuleData) => {
            if (module.id && module.title) {
              moduleIdToTitle.set(module.id, module.title);
            }
          });
        }

        // Récupérer toutes les ressources
        const response = await fetch(
          `${API_ENDPOINTS.RESOURCES.GET_RESOURCES}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Organiser les ressources par module
        const moduleMap = new Map<string, Module>();

        // Définition des couleurs d'arrière-plan par défaut pour les modules
        const bgColors = ["bg-[#fff8eb]", "bg-[#f8fafc]"];

        // Regrouper les ressources par moduleId
        data.forEach((resource: Resource) => {
          const moduleId = resource.moduleId || "default";

          if (!moduleMap.has(moduleId)) {
            // Récupérer le titre du module depuis le mapping
            const moduleTitle = moduleIdToTitle.get(moduleId);
            const moduleIndex = moduleMap.size + 1;

            // Créer un nouveau module s'il n'existe pas déjà
            moduleMap.set(moduleId, {
              id: moduleId,
              title: `Module ${moduleIndex} : ${moduleTitle || "Sans titre"}`,
              bgColor: bgColors[moduleMap.size % bgColors.length],
              resources: [],
            });
          }

          // Ajouter la ressource au module correspondant
          const module = moduleMap.get(moduleId);
          if (module) {
            module.resources.push(resource);
          }
        });

        // Convertir la Map en tableau
        setModules(Array.from(moduleMap.values()));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Une erreur est survenue"
        );
        console.error("Erreur lors de la récupération des ressources:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // Fonction pour télécharger une ressource
  const handleDownloadResource = async (resourceId: number) => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Effectuer la requête fetch avec l'en-tête Authorization
      const response = await fetch(
        API_ENDPOINTS.RESOURCES.DOWNLOAD_RESOURCE(resourceId.toString()),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléchargement du fichier");
      }

      // Récupérer le blob de la réponse
      const blob = await response.blob();

      // Créer une URL d'objet pour le blob
      const url = window.URL.createObjectURL(blob);

      // Créer un élément <a> temporaire pour déclencher le téléchargement
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      // Essayer d'obtenir le nom du fichier depuis les en-têtes de réponse
      const contentDisposition = response.headers.get("content-disposition");
      let filename = `resource-${resourceId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;

      // Ajouter l'élément au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(a);
      a.click();

      // Nettoyer
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Erreur lors du téléchargement:", err);
      alert("Erreur lors du téléchargement de la ressource");
    }
  };

  // Fonction pour faire fonctionner les carrousels
  const scrollCarousel = (moduleId: string, direction: "left" | "right") => {
    const container = document.getElementById(`carousel-${moduleId}`);
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      if (direction === "left") {
        container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  // Composant pour afficher une ressource
  const ResourceCard = ({ resource }: { resource: Resource }) => {
    // Fonction pour déterminer l'icône à afficher en fonction du type de document
    const getResourceIcon = () => {
      // Vérifier si une image existe
      if (resource.image && resource.image !== "") {
        return (
          <img
            src={resource.image}
            alt={resource.title}
            className="w-full h-full object-cover"
          />
        );
      }

      // Définir une icône par défaut basée sur le type de ressource
      let IconComponent = File; // Icône par défaut
      let iconColor = "#6B7280"; // Couleur grise par défaut

      // Vérifier et afficher le type pour déboguer
      console.log("Document:", resource.title);
      console.log("Filename:", resource.filename);
      console.log("Mimetype:", resource.mimetype);
      console.log("Type:", resource.type);

      // Détection basée sur le mimetype, le filename, et en dernier recours le type
      const detectDocumentType = () => {
        // Vérifier d'abord le mimetype qui est le plus fiable
        if (resource.mimetype) {
          const mimetype = resource.mimetype.toLowerCase();

          if (mimetype === "application/pdf") {
            return "pdf";
          } else if (
            mimetype === "application/msword" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ) {
            return "word";
          } else if (
            mimetype === "application/vnd.ms-excel" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            mimetype === "text/csv"
          ) {
            return "excel";
          } else if (
            mimetype === "application/vnd.ms-powerpoint" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) {
            return "powerpoint";
          } else if (mimetype.startsWith("image/")) {
            return "image";
          } else if (mimetype.startsWith("video/")) {
            return "video";
          } else if (mimetype.startsWith("audio/")) {
            return "audio";
          } else if (
            mimetype === "application/zip" ||
            mimetype === "application/x-zip-compressed" ||
            mimetype === "application/x-rar-compressed"
          ) {
            return "archive";
          } else if (
            mimetype === "text/html" ||
            mimetype === "application/javascript" ||
            mimetype === "text/css"
          ) {
            return "code";
          }
        }

        // Vérifier ensuite le filename qui est assez fiable
        if (resource.filename) {
          const fileName = resource.filename.toLowerCase();

          if (fileName.endsWith(".pdf")) {
            return "pdf";
          } else if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) {
            return "word";
          } else if (
            fileName.endsWith(".xls") ||
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".csv")
          ) {
            return "excel";
          } else if (fileName.endsWith(".ppt") || fileName.endsWith(".pptx")) {
            return "powerpoint";
          } else if (
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".png") ||
            fileName.endsWith(".gif")
          ) {
            return "image";
          } else if (
            fileName.endsWith(".mp4") ||
            fileName.endsWith(".mov") ||
            fileName.endsWith(".avi")
          ) {
            return "video";
          } else if (
            fileName.endsWith(".mp3") ||
            fileName.endsWith(".wav") ||
            fileName.endsWith(".ogg")
          ) {
            return "audio";
          } else if (fileName.endsWith(".zip") || fileName.endsWith(".rar")) {
            return "archive";
          } else if (
            fileName.endsWith(".html") ||
            fileName.endsWith(".js") ||
            fileName.endsWith(".css")
          ) {
            return "code";
          }
        }

        // En dernier recours, essayer le champ type
        if (resource.type) {
          const type = resource.type.toLowerCase();

          if (type.includes("pdf")) {
            return "pdf";
          } else if (type.includes("word") || type.includes("doc")) {
            return "word";
          } else if (
            type.includes("excel") ||
            type.includes("xls") ||
            type.includes("csv")
          ) {
            return "excel";
          } else if (type.includes("powerpoint") || type.includes("ppt")) {
            return "powerpoint";
          } else if (
            type.includes("image") ||
            type.includes("jpg") ||
            type.includes("jpeg") ||
            type.includes("png") ||
            type.includes("gif")
          ) {
            return "image";
          } else if (type.includes("video") || type.includes("mp4")) {
            return "video";
          } else if (
            type.includes("audio") ||
            type.includes("mp3") ||
            type.includes("wav")
          ) {
            return "audio";
          } else if (type.includes("zip") || type.includes("rar")) {
            return "archive";
          } else if (
            type.includes("code") ||
            type.includes("html") ||
            type.includes("js") ||
            type.includes("css")
          ) {
            return "code";
          }
        }

        // Enfin, essayer le titre comme dernier recours
        if (resource.title) {
          const title = resource.title.toLowerCase();

          if (title.endsWith(".pdf")) {
            return "pdf";
          } else if (title.endsWith(".doc") || title.endsWith(".docx")) {
            return "word";
          } else if (
            title.endsWith(".xls") ||
            title.endsWith(".xlsx") ||
            title.endsWith(".csv")
          ) {
            return "excel";
          } else if (title.endsWith(".ppt") || title.endsWith(".pptx")) {
            return "powerpoint";
          }
        }

        return null; // Aucun type reconnu
      };

      // Déterminer le type de document
      const documentType = detectDocumentType();
      console.log("Type détecté:", documentType);

      // Définir l'icône en fonction du type détecté
      if (documentType === "pdf") {
        IconComponent = FileType;
        iconColor = "#EC4899"; // Rose
      } else if (documentType === "word") {
        IconComponent = FileText;
        iconColor = "#3B82F6"; // Bleu
      } else if (documentType === "excel") {
        IconComponent = FileSpreadsheet;
        iconColor = "#10B981"; // Vert
      } else if (documentType === "powerpoint") {
        IconComponent = FileText;
        iconColor = "#F97316"; // Orange
      } else if (documentType === "image") {
        IconComponent = Image;
        iconColor = "#8B5CF6"; // Violet
      } else if (documentType === "video") {
        IconComponent = Video;
        iconColor = "#EF4444"; // Rouge
      } else if (documentType === "audio") {
        IconComponent = Music;
        iconColor = "#F59E0B"; // Jaune
      } else if (documentType === "archive") {
        IconComponent = Archive;
        iconColor = "#6B7280"; // Gris
      } else if (documentType === "code") {
        IconComponent = Code;
        iconColor = "#0EA5E9"; // Bleu ciel
      }

      // Afficher l'icône avec la couleur appropriée
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-50">
          <div className="flex flex-col items-center justify-center">
            <IconComponent size={64} color={iconColor} strokeWidth={1.5} />
            <div className="mt-2 text-sm font-medium text-gray-600">
              {resource.type || "Document"}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="min-w-[250px] max-w-[250px] bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col">
        <div className="h-36 bg-gray-50 relative flex items-center justify-center">
          {resource.type && (
            <div className="absolute top-2 left-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
              {resource.type}
            </div>
          )}
          {resource.badge && (
            <div className="absolute text-2xl font-bold text-white bg-red-600 px-3 py-1 transform rotate-0 z-10">
              {resource.badge}
            </div>
          )}
          {getResourceIcon()}
        </div>
        <div className="p-4 flex flex-col flex-grow">
          <h3 className="font-semibold text-gray-800 mb-2 text-center">
            {resource.title}
          </h3>
          <p className="text-xs text-gray-500 mb-3 text-center">
            {resource.description}
          </p>
          <button
            onClick={() => handleDownloadResource(resource.id)}
            className="w-full py-1.5 text-white bg-orange-500 hover:bg-orange-600 rounded text-sm flex items-center justify-center"
          >
            <span>Télécharger</span>
          </button>
        </div>
      </div>
    );
  };

  // Affichage du chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p>Chargement des ressources pédagogiques...</p>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-500">Erreur: {error}</p>
      </div>
    );
  }

  // Si aucun module n'est disponible
  if (modules.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p>Aucune ressource pédagogique n'est disponible pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Titre principal */}
      <div className="bg-white py-6 relative">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl font-bold text-left text-gray-800">
            Ressources pédagogiques
          </h1>

          {/* Image positionnée sur la bannière - visible uniquement sur grands écrans */}
          <div className="absolute right-10 pr-10 top-[-10px] z-30 hidden lg:block">
            <img
              src="/images/IMAGES/logoFichier-13.png"
              alt="Éducation"
              className="w-auto h-[600px] object-contain drop-shadow-xl"
            />
          </div>
        </div>
      </div>

      {/* Bannière en haut */}
      <div className="bg-[#e6f7ff] py-10 px-4 relative overflow-hidden">
        <div className="container mx-auto flex flex-col lg:flex-row items-center justify-center">
          <div className="w-full lg:max-w-3xl z-10 text-center">
            <h1 className="text-2xl font-bold text-orange-500 mb-2">
              Retrouvez ici toutes vos ressources pédagogiques <br />
              et vos documents de cours accessibles partout et en tout temps
            </h1>
          </div>
        </div>
      </div>

      {/* Modules de ressources */}
      {modules.map((module) => (
        <div
          key={module.id}
          className={`container mx-auto px-4 py-8 ${
            module.id === "module-2" ? "bg-gray-50" : ""
          }`}
        >
          <h2 className="text-xl font-bold uppercase mb-6">{module.title}</h2>

          <div className="relative">
            <button
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 hover:bg-gray-100"
              aria-label="Précédent"
              onClick={() => scrollCarousel(module.id, "left")}
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>

            <div
              id={`carousel-${module.id}`}
              className={`flex overflow-x-auto gap-4 py-2 px-6 ${module.bgColor} hide-scrollbar`}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {module.resources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>

            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 hover:bg-gray-100"
              aria-label="Suivant"
              onClick={() => scrollCarousel(module.id, "right")}
            >
              <ChevronRight size={24} className="text-gray-700" />
            </button>
          </div>
        </div>
      ))}

      <div className="py-10 px-4 relative overflow-hidden">
        <div className="container mx-auto flex flex-col lg:flex-row items-center justify-center">
          <div className="w-full lg:max-w-3xl z-10 text-center">
            <h1 className="text-2xl font-bold mb-2">
              Accédez en toute securité et en toute confiance à vos ressources
              pédagogiques.
            </h1>
          </div>
        </div>
      </div>

      {/* Style pour cacher la scrollbar */}
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

export default RessourcesPedagogiques;
